import { useState, type ReactNode } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/badge/chip";
import { ResultBadge } from "@/components/badge/result-badge";
import { EllipsisIcon, InfoIcon, RotateCwIcon, TrashIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { useReportDocs } from "@/hooks/use-report-docs";
import type { VersionReportDoc } from "@/types/domain";
import { DATE_SHORT } from "@/lib/date";
import {
  ReportDeleteDialog,
  ReportDocLink,
  ReportSummaryEditor,
} from "./report-doc-atoms";

type ReportDocsHandle = ReturnType<typeof useReportDocs>;

/** Summary block shared by the hover popover and the info-icon popover. */
const SummaryContent = ({
  doc,
  docs,
  editable,
}: {
  doc: VersionReportDoc;
  docs: ReportDocsHandle;
  editable: boolean;
}) => (
  <div className="text-sm">
    <div className="mb-2 flex items-center justify-between">
      <p className="font-medium">Summary</p>
      {editable && !docs.editingDocUrl && (
        <Button
          variant="ghost"
          size="sm"
          className="h-6"
          onClick={(e) => {
            e.stopPropagation();
            docs.startEditSummary(doc);
          }}
        >
          Edit
        </Button>
      )}
    </div>
    {editable && docs.editingDocUrl === doc.url ? (
      <ReportSummaryEditor
        value={docs.editingSummary}
        onChange={docs.setEditingSummary}
        onCancel={docs.cancelEditSummary}
        onSave={() => docs.saveSummary(doc)}
        isSaving={docs.isSavingSummary}
      />
    ) : (
      <p className="whitespace-pre-wrap text-muted-foreground">{doc.summary}</p>
    )}
  </div>
);

/**
 * One report-doc row, shared by the PRD report editor (`actions="menu"`:
 * summary editable, app chip + date, resend/delete behind a ⋯ menu — the
 * delete confirm dialog stays in the container, keyed by docs.deleteUrl) and
 * the engagement panel (`actions="inline"`: leading result badge, bare
 * resend/info/delete buttons with a per-row confirm dialog).
 */
export const ReportDocRow = ({
  doc,
  docs,
  actions,
  appTag,
}: {
  doc: VersionReportDoc;
  docs: ReportDocsHandle;
  actions: "menu" | "inline";
  /** Which app this report's version belongs to (menu variant only). */
  appTag?: { appName: string; docName?: string };
}) => {
  const [hoverOpen, setHoverOpen] = useState(false);
  const isProduction = import.meta.env.PROD;
  const menu = actions === "menu";
  const linkClassName = menu ? "block" : "block max-w-[200px]";

  const link: ReactNode = doc.summary ? (
    <Popover open={hoverOpen} onOpenChange={setHoverOpen}>
      <PopoverTrigger asChild>
        <span
          className={menu ? "min-w-0 flex-1" : undefined}
          onMouseEnter={() => setHoverOpen(true)}
          onMouseLeave={() => setHoverOpen(false)}
        >
          <ReportDocLink doc={doc} className={linkClassName} />
        </span>
      </PopoverTrigger>
      <PopoverContent
        className={menu ? "w-[40rem] max-w-[90vw]" : "w-80"}
        onMouseEnter={() => setHoverOpen(true)}
        onMouseLeave={() => setHoverOpen(false)}
      >
        <SummaryContent doc={doc} docs={docs} editable={menu} />
      </PopoverContent>
    </Popover>
  ) : (
    <ReportDocLink
      doc={doc}
      className={cn(menu && "min-w-0 flex-1", !menu && "max-w-[200px]")}
    />
  );

  if (!menu) {
    return (
      <div className="flex items-center space-x-2">
        {doc.result && (
          <ResultBadge result={doc.result} className="whitespace-nowrap" />
        )}
        {link}
        {!isProduction && (
          <Button
            variant="ghost"
            size="sm"
            className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
            onClick={() => docs.resendWebhook(doc.url)}
            disabled={docs.resendingDocUrl === doc.url}
            title="Resend feishu message"
          >
            <RotateCwIcon
              className={`h-3 w-3 ${docs.resendingDocUrl === doc.url ? "animate-spin" : ""}`}
            />
          </Button>
        )}
        {doc.summary && (
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-4 w-4 p-0 text-muted-foreground hover:text-foreground"
              >
                <InfoIcon className="h-3 w-3" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <SummaryContent doc={doc} docs={docs} editable={false} />
            </PopoverContent>
          </Popover>
        )}
        <ReportDeleteDialog
          open={docs.deleteUrl === doc.url}
          onCancel={() => docs.askDelete(null)}
          onConfirm={docs.confirmDelete}
          isMutating={docs.isMutating}
        >
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-destructive hover:text-destructive"
            onClick={() => docs.askDelete(doc.url)}
            disabled={docs.isMutating}
          >
            <TrashIcon className="h-3 w-3" />
          </Button>
        </ReportDeleteDialog>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-2">
      {link}
      {/* Self-contained provider: the row renders under callers with and
          without one (PRD cell vs version engagement panel) — Radix throws
          without it. */}
      {appTag && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Chip tone="green" className="h-6 px-2 leading-6">
                {appTag.appName}
              </Chip>
            </TooltipTrigger>
            <TooltipContent>
              <p>{appTag.docName}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {doc.result && <ResultBadge result={doc.result} />}
      <span
        title="Created at"
        className="w-14 shrink-0 text-right text-xs tabular-nums text-muted-foreground"
      >
        {doc.createTime ? format(new Date(doc.createTime), DATE_SHORT) : ""}
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 shrink-0 p-0 text-muted-foreground hover:text-foreground"
            title="More actions"
          >
            <EllipsisIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {!isProduction && (
            <DropdownMenuItem
              onClick={() => docs.resendWebhook(doc.url)}
              disabled={docs.resendingDocUrl === doc.url}
            >
              <RotateCwIcon
                className={`mr-2 h-3.5 w-3.5 ${docs.resendingDocUrl === doc.url ? "animate-spin" : ""}`}
              />
              Resend Feishu
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            onClick={() => docs.askDelete(doc.url)}
            className="text-destructive focus:text-destructive"
          >
            <TrashIcon className="mr-2 h-3.5 w-3.5" />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
